import { motion } from 'framer-motion';
import {
  Calendar,
  CheckCircle,
  Clock,
  Globe,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  User,
} from 'lucide-react';
import React, { useState } from 'react';

import { Footer } from '@components/Footer';
import { Header } from '@components/Header';
import { SEOHead } from '@components/SEOHead';

import { Button } from '@ui/Button';
import { Hero } from '@ui/Hero';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO, VALIDATION } from '@constants/contact';

export const ContactPage = () => {
  // WhatsApp Business Number from constants
  const whatsappNumber = CONTACT_INFO.phone.whatsapp;

  // Form error state
  const [formError, setFormError] = useState<string>('');

  // Clear errors when user starts typing
  const clearError = () => {
    if (formError) setFormError('');
  };

  // Handle form submission and send WhatsApp message
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Clear any previous errors
    setFormError('');

    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const service = formData.get('service') as string;
    const message = formData.get('message') as string;

    // Validate required fields
    if (!name || !email || !service || !message) {
      setFormError('Please fill in all required fields.');
      return;
    }

    // Validate email format
    if (!VALIDATION.email.test(email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    const serviceLabels: { [key: string]: string } = {
      'new-investor': 'New to investing',
      'goal-planning': 'Goal-based planning',
      'portfolio-review': 'Portfolio review',
      'sip-planning': 'SIP planning',
    };

    const whatsappMessage = `*${CONTACT_INFO.company.name} Consultation Request*

*Name:* ${name}
*Email:* ${email}
*Service:* ${serviceLabels[service] || 'General Inquiry'}${
      message
        ? `

*Message:*
${message}`
        : ''
    }`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(whatsappMessage);

    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');

    // Reset form and clear errors
    e.currentTarget.reset();
    setFormError('');
  };

  // Contact methods
  const contactMethods = [
    {
      icon: Phone,
      title: 'Call us',
      description: 'Speak directly with us for personalized guidance',
      value: CONTACT_INFO.phone.display,
      action: `tel:${CONTACT_INFO.phone.primary}`,
      buttonText: 'Call now',
      highlight: false,
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'Quick and convenient messaging for instant responses',
      value: CONTACT_INFO.phone.display,
      action: `https://wa.me/${CONTACT_INFO.phone.whatsapp}?text=Hi ${CONTACT_INFO.contact_person.short_name}, I would like to discuss my investment needs.`,
      buttonText: 'Message on WhatsApp',
      highlight: true,
    },
    {
      icon: Mail,
      title: 'Email',
      description: 'Send detailed queries and receive comprehensive responses',
      value: CONTACT_INFO.email.general,
      action: `mailto:${CONTACT_INFO.email.general}?subject=Investment Inquiry`,
      buttonText: 'Send email',
      highlight: false,
    },
  ];

  // Office information
  const officeInfo = [
    {
      icon: MapPin,
      title: 'Office address',
      type: 'address' as const,
      details: [CONTACT_INFO.address.full],
      mapLink: CONTACT_INFO.address.mapLink,
    },
    {
      icon: Clock,
      title: 'Business hours',
      type: 'hours' as const,
      hourDetails: [
        { label: 'Weekdays', value: CONTACT_INFO.hours.weekdays },
        { label: 'Timezone', value: CONTACT_INFO.hours.timezone },
        { label: 'Weekends', value: 'By appointment' },
        { label: 'Emergency', value: 'Available on WhatsApp' },
      ],
    },
  ];

  // Why contact us reasons
  const whyContactUs = [
    {
      icon: User,
      title: 'Personalized consultation',
      description:
        'Get tailored investment advice based on your unique financial situation and goals.',
    },
    {
      icon: CheckCircle,
      title: 'Expert guidance',
      description:
        'Benefit from AMFI certified expertise in mutual funds and financial planning.',
    },
    {
      icon: Calendar,
      title: 'Flexible scheduling',
      description:
        'Book consultations at your convenience, including weekend appointments.',
    },
    {
      icon: Globe,
      title: 'Comprehensive planning',
      description:
        'From goal planning to tax optimization, get complete financial solutions.',
    },
  ];

  return (
    <>
      <SEOHead
        title={`Contact us | ${CONTACT_INFO.company.name}`}
        description={`Contact ${CONTACT_INFO.contact_person.full_name}, ${CONTACT_INFO.contact_person.designation} for personalized investment advice. Call, WhatsApp, or email for financial planning consultation.`}
        keywords={`contact niveshright, ${CONTACT_INFO.contact_person.full_name} contact, investment advisor contact, mutual fund distributor, financial planning consultation`}
        canonicalUrl="https://niveshright.com/contact"
      />

      <div className="min-h-screen" style={{ backgroundColor: COLORS.pearl }}>
        <Header />

        <Hero
          title={
            <>
              Contact <span style={{ color: COLORS.gold }}>us</span>
            </>
          }
          description="Ready to start your investment journey? Get in touch with us for personalized financial guidance and expert advice."
          variant="gradient"
        >
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div
                className="text-3xl font-bold"
                style={{ color: COLORS.gold }}
              >
                Quick
              </div>
              <div className="text-white/70">response time</div>
            </div>
            <div className="text-center">
              <div
                className="text-3xl font-bold"
                style={{ color: COLORS.sage }}
              >
                Personal
              </div>
              <div className="text-white/70">consultation</div>
            </div>
            <div className="text-center">
              <div
                className="text-3xl font-bold"
                style={{ color: COLORS.accent }}
              >
                Expert
              </div>
              <div className="text-white/70">guidance</div>
            </div>
          </div>
        </Hero>

        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                Why choose our consultation?
              </h2>
              <p className="text-xl" style={{ color: COLORS.smoke }}>
                Experience the difference of personalized financial guidance
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyContactUs.map((reason, index) => {
                const IconComponent = reason.icon;
                return (
                  <motion.div
                    key={reason.title}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center hover:shadow-xl transition-all duration-300"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  >
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto"
                      style={{ backgroundColor: `${COLORS.accent}15` }}
                    >
                      <IconComponent
                        size={28}
                        style={{ color: COLORS.accent }}
                      />
                    </div>
                    <h3
                      className="text-lg font-bold mb-3"
                      style={{ color: COLORS.primary }}
                    >
                      {reason.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {reason.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>

        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.mist }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                Get in touch
              </h2>
              <p className="text-xl" style={{ color: COLORS.smoke }}>
                Choose your preferred way to connect with us
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {contactMethods.map((method, index) => {
                const IconComponent = method.icon;
                return (
                  <motion.div
                    key={method.title}
                    className={`bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center ${
                      method.highlight
                        ? 'ring-2 ring-blue-500 ring-opacity-50'
                        : ''
                    }`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.1 + index * 0.1 }}
                  >
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto"
                      style={{
                        backgroundColor: `${method.highlight ? COLORS.accent : COLORS.sage}15`,
                      }}
                    >
                      <IconComponent
                        size={32}
                        style={{
                          color: method.highlight ? COLORS.accent : COLORS.sage,
                        }}
                      />
                    </div>
                    <h3
                      className="text-xl font-bold mb-3"
                      style={{ color: COLORS.primary }}
                    >
                      {method.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {method.description}
                    </p>
                    <p
                      className="text-lg font-semibold mb-6"
                      style={{ color: COLORS.accent }}
                    >
                      {method.value}
                    </p>
                    <Button
                      href={method.action}
                      target={
                        method.title === 'WhatsApp' ? '_blank' : undefined
                      }
                      rel={
                        method.title === 'WhatsApp'
                          ? 'noopener noreferrer'
                          : undefined
                      }
                      variant={method.highlight ? 'accent' : 'sage'}
                      fullWidth
                      icon={
                        method.title === 'WhatsApp'
                          ? MessageCircle
                          : method.title === 'Email'
                            ? Send
                            : Phone
                      }
                      iconSize={16}
                    >
                      {method.buttonText}
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>

        {/* Contact Form Section */}
        <motion.section
          className="py-20"
          style={{
            background: `linear-gradient(135deg, ${COLORS.secondary} 0%, ${COLORS.primary} 100%)`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2
                className="text-4xl font-bold mb-6"
                style={{ color: COLORS.pearl }}
              >
                Request free consultation
              </h2>
              <p className="text-xl text-white/80 mb-8">
                Book a free consultation and discover how to turn your salary
                into serious wealth. No sales pressure, just honest advice about
                your financial future.
              </p>
            </div>

            <motion.div
              className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.6 }}
            >
              <form
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                onSubmit={handleFormSubmit}
              >
                {/* Error Message Display */}
                {formError && (
                  <div className="sm:col-span-2 mb-4">
                    <motion.div
                      className="bg-red-500/20 backdrop-blur-sm border border-red-400/30 rounded-2xl p-4 text-center"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <svg
                          className="w-5 h-5 text-red-300"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-red-200 font-medium">
                          {formError}
                        </span>
                      </div>
                    </motion.div>
                  </div>
                )}

                <div className="space-y-4 sm:col-span-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      name="name"
                      placeholder="Full name *"
                      className="p-4 rounded-2xl border-0 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 focus:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                      onChange={clearError}
                    />
                    <input
                      name="email"
                      type="email"
                      placeholder="Email address *"
                      className="p-4 rounded-2xl border-0 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 focus:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                      onChange={clearError}
                    />
                  </div>

                  <select
                    name="service"
                    className="p-4 pr-12 rounded-2xl border-0 bg-white/20 backdrop-blur-sm text-white focus:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300 w-full appearance-none bg-no-repeat bg-right bg-[length:16px_16px]"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 1rem center',
                    }}
                    onChange={clearError}
                  >
                    <option value="" className="text-gray-800">
                      Select service *
                    </option>
                    <option value="new-investor" className="text-gray-800">
                      I'm new to investing
                    </option>
                    <option value="goal-planning" className="text-gray-800">
                      I have specific goals (house, education, etc.)
                    </option>
                    <option value="portfolio-review" className="text-gray-800">
                      Review my existing investments
                    </option>
                    <option value="sip-planning" className="text-gray-800">
                      Start SIP investments
                    </option>
                  </select>

                  <textarea
                    name="message"
                    placeholder="Message *"
                    rows={4}
                    className="w-full p-4 rounded-2xl border-0 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 focus:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300 resize-none"
                    onChange={clearError}
                  />
                </div>

                <div className="sm:col-span-2 flex flex-col items-center space-y-4 pt-4">
                  <Button type="submit" variant="primary" size="lg">
                    Request free consultation
                  </Button>

                  <div className="flex items-center space-x-4 text-white/60 text-sm">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: COLORS.sage }}
                      ></div>
                      <span>Free consultation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: COLORS.accent }}
                      ></div>
                      <span>No obligation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: COLORS.gold }}
                      ></div>
                      <span>AMFI certified</span>
                    </div>
                  </div>
                </div>
              </form>
            </motion.div>

            <motion.div
              className="mt-8 text-white/60 text-sm text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.8 }}
            >
              AMFI registered ARN:{' '}
              <span className="font-semibold text-white/80">
                {CONTACT_INFO.amfi.arn}
              </span>
            </motion.div>
          </div>
        </motion.section>

        {/* Office Information */}
        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.9 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                Office information
              </h2>
              <p className="text-xl" style={{ color: COLORS.smoke }}>
                Visit us or schedule a consultation
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {officeInfo.map((info, index) => {
                const IconComponent = info.icon;
                return (
                  <motion.div
                    key={info.title}
                    className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 2.1 + index * 0.1 }}
                  >
                    {/* Header with icon and title */}
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="flex-shrink-0">
                        <div
                          className="w-12 h-12 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                          style={{ backgroundColor: `${COLORS.gold}15` }}
                        >
                          <IconComponent
                            size={24}
                            style={{ color: COLORS.gold }}
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-xl font-bold"
                          style={{ color: COLORS.primary }}
                        >
                          {info.title}
                        </h3>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-3">
                      {info.type === 'address' ? (
                        <>
                          {info.details.map((detail, idx) => (
                            <p
                              key={idx}
                              className="text-gray-600 leading-relaxed"
                            >
                              {detail}
                            </p>
                          ))}
                          <a
                            href={info.mapLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-300 font-medium mt-3 group/link"
                          >
                            <MapPin
                              size={16}
                              className="mr-2 group-hover/link:scale-110 transition-transform duration-200"
                            />
                            View on Google Maps
                          </a>
                        </>
                      ) : info.type === 'hours' ? (
                        <div className="space-y-2">
                          {info.hourDetails?.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center py-1"
                            >
                              <span className="font-semibold text-gray-700">
                                {item.label}:
                              </span>
                              <span className="text-gray-600">
                                {item.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>

        <Footer />
      </div>
    </>
  );
};
